"use client";

import { useState } from "react";
import Image from "next/image";
import { Play } from "lucide-react";
import { youtubeEmbedUrl, youtubeThumb } from "@/lib/v2/video";

/** One YouTube video in a 16:9 box: thumbnail first, the player loads on click. */
export function PageVideo({
  video,
  className = "",
  tone = "light",
}: {
  video: { id: string; title: string | null };
  className?: string;
  tone?: "light" | "dark";
}) {
  const [playing, setPlaying] = useState(false);
  const label = video.title ? `Play video: ${video.title}` : "Play video";

  return (
    <div
      className={`relative aspect-video w-full overflow-hidden rounded-2xl ${tone === "dark" ? "bg-black" : "bg-surface"} ${className}`}
    >
      {playing ? (
        <iframe
          src={youtubeEmbedUrl(video.id)}
          title={video.title ?? "YouTube video"}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 size-full border-0"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={label}
          className="group absolute inset-0 size-full rounded-2xl focus-visible:outline-none"
        >
          <Image
            src={youtubeThumb(video.id)}
            alt=""
            fill
            unoptimized
            sizes="(min-width: 1024px) 896px, 100vw"
            className="object-cover"
          />
          {/* Focus ring sits on this layer so the thumbnail does not paint over it. */}
          <span
            className="absolute inset-0 rounded-2xl bg-black/10 transition-colors group-hover:bg-black/20 group-focus-visible:ring-2 group-focus-visible:ring-inset group-focus-visible:ring-brand-500"
            aria-hidden="true"
          />
          <span
            className="absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg transition-transform group-hover:scale-105"
            aria-hidden="true"
          >
            <Play className="ml-1 size-7 fill-current" />
          </span>
        </button>
      )}
    </div>
  );
}
